/**
 * BranchHero - the S-BRANCH context bar (UX-RW-03 v1.1 S-BRANCH): the TPIN
 * verbatim with the branch named under it, Change TPIN, the period selector
 * and the BRANCH's source freshness with the explicit Refresh action.
 *
 * Presentational. Refresh is a button the user presses; nothing here schedules
 * a re-read (PD-33). The screen's single h1 is the branch title below this bar,
 * so the TPIN here is a plain line, not a heading. Long values wrap.
 */

import type { PeriodSelection } from '@domain/types'
import type { FreshnessState } from '@domain/tpin'
import { branchPath } from '@domain/tpinPaths'
import { formatFreshness } from './FreshnessIndicator'
import { Link } from './Link'
import { PeriodSelector } from './PeriodSelector'
import { REFRESHING_LABEL, REFRESH_LABEL, REFRESH_NOTE } from './refreshCopy'

/** The branch row holds no source timestamp: the honest absence, never a page-load time. */
export const NO_BRANCH_TIMESTAMP_TEXT = 'No data timestamp from source'

/** The freshness line of a branch-scoped screen: the source stamp, the honest absence, or the loading word. */
export function freshnessLabel(freshness: FreshnessState): string {
  if (freshness.kind === 'pending') return 'Loading…'
  if (freshness.kind === 'absent') return NO_BRANCH_TIMESTAMP_TEXT
  return formatFreshness(freshness.raw)
}

export function BranchHero({
  tin,
  dbName,
  period,
  freshness,
  refreshing,
  onRefresh,
}: {
  readonly tin: string
  readonly dbName: string
  readonly period: PeriodSelection
  readonly freshness: FreshnessState
  readonly refreshing: boolean
  readonly onRefresh: () => void
}) {
  return (
    <section className="dash-hero" aria-label="Branch context" data-testid="branch-hero">
      <div className="dash-hero-row">
        <div className="dash-hero-id">
          <p className="dash-hero-tin" data-testid="branch-hero-tin">{`TPIN ${tin}`}</p>
          <p className="dash-hero-branches" data-testid="branch-hero-branch">{`Branch ${dbName}`}</p>
        </div>
        <Link to="/" period={period} className="dash-hero-change" testid="change-tpin">
          Change TPIN
        </Link>
      </div>
      <div className="dash-hero-row">
        <div className="dash-freshness" aria-live="polite" data-testid="dash-freshness">
          <span className="dash-freshness-label">Source data as of:</span>
          <span className="dash-freshness-value" data-testid="dash-freshness-value">
            {freshnessLabel(freshness)}
          </span>
          <span className="dash-freshness-note" data-testid="refresh-note">
            {REFRESH_NOTE}
          </span>
        </div>
        <button
          type="button"
          className="dash-hero-refresh"
          onClick={onRefresh}
          disabled={refreshing}
          aria-busy={refreshing || undefined}
          data-testid="refresh-button"
        >
          {refreshing ? REFRESHING_LABEL : REFRESH_LABEL}
        </button>
      </div>
      <div className="dash-hero-period">
        <PeriodSelector period={period} pathname={branchPath(tin, dbName)} />
      </div>
    </section>
  )
}
